import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { fetchCurrentUser } from '../../ducks/user.duck';

// Import ManageListingsPage thunks for reuse (DRY)
import {
  queryOwnListings,
  queryOwnListingsThunk,
  closeListing,
  openListing,
  discardDraft,
  getOwnListingsById,
  loadData as manageListingsLoadData,
} from '../ManageListingsPage/ManageListingsPage.duck';

// Re-export for convenience in the component
export { closeListing, openListing, discardDraft, getOwnListingsById };

// Helper to extract transaction earnings
const extractTransactionEarnings = tx => {
  if (!tx || !tx.attributes) return 0;
  const payoutTotal = tx.attributes.payoutTotal;
  const payinTotal = tx.attributes.payinTotal;
  if (payoutTotal && typeof payoutTotal.amount === 'number') {
    return payoutTotal.amount / 100;
  }
  if (payinTotal && typeof payinTotal.amount === 'number') {
    return payinTotal.amount / 100;
  }
  return 0;
};

// Async thunk to fetch all seller dashboard data in parallel
export const fetchSellerDashboardDataThunk = createAsyncThunk(
  'SellerDashboardPage/fetchSellerDashboardData',
  async (config, { dispatch, getState, extra: sdk, rejectWithValue }) => {
    try {
      const state = getState();
      const currentUser = state.user?.currentUser;
      const currentUserId = currentUser?.id?.uuid || currentUser?.id;

      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config?.layout?.listingImage || {};
      const aspectRatio = aspectHeight / aspectWidth;

      // 1. Fetch own listings via ManageListingsPage queryOwnListingsThunk
      //    This populates state.ManageListingsPage.ownEntities and currentPageResultIds automatically
      const listingsPromise = dispatch(
        queryOwnListingsThunk({
          perPage: 100,
          include: ['images', 'currentStock'],
          'fields.image': [
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          'limit.images': 1,
        })
      ).unwrap();

      // 2. Fetch sales transactions with customer, listing, images
      const salesPromise = sdk.transactions.query({
        only: 'sale',
        perPage: 100,
        include: [
          'customer',
          'customer.profileImage',
          'listing',
          'listing.images',
          'booking',
        ],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          'variants.square-small',
          'variants.square-small2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      });

      // 3. Fetch reviews
      const reviewsPromise = currentUserId
        ? sdk.reviews.query({
            subject_id: currentUserId,
            state: 'public',
            include: ['author', 'author.profileImage'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
          })
        : Promise.resolve({ data: { data: [] } });

      const [listingsRes, salesRes, reviewsRes] = await Promise.all([
        listingsPromise,
        salesPromise,
        reviewsPromise,
      ]);

      // listingsRes from queryOwnListings is already processed — grab the pagination
      // to extract total count
      const updatedState = getState();
      const manageListingsState = updatedState.ManageListingsPage;
      const listingIds = manageListingsState.currentPageResultIds || [];

      // Get denormalized listings from ManageListingsPage store to count active ones
      const listings = getOwnListingsById(updatedState, listingIds);

      // Denormalize sales & reviews
      const sales = denormalisedResponseEntities(salesRes);
      const reviews = denormalisedResponseEntities(reviewsRes);

      // Calculate metrics
      const activeListingsCount = listings.filter(
        l => l.attributes && l.attributes.state === 'published'
      ).length;
      const totalListingsCount = listings.length;
      const ordersCount = sales.length;
      const messagesCount = sales.length;

      // Total Earnings
      let totalEarningsAmount = 0;
      let currency = 'USD';
      sales.forEach(tx => {
        const lastTransition = tx.attributes?.lastTransition;
        if (
          lastTransition &&
          !lastTransition.includes('cancel') &&
          !lastTransition.includes('decline') &&
          !lastTransition.includes('expire')
        ) {
          totalEarningsAmount += extractTransactionEarnings(tx);
          if (tx.attributes?.payinTotal?.currency) {
            currency = tx.attributes.payinTotal.currency;
          }
        }
      });

      // Average Rating
      let totalRatingSum = 0;
      let reviewsCount = 0;
      reviews.forEach(rev => {
        if (typeof rev.attributes?.rating === 'number') {
          totalRatingSum += rev.attributes.rating;
          reviewsCount += 1;
        }
      });
      const averageRating = reviewsCount > 0 ? (totalRatingSum / reviewsCount).toFixed(1) : 0;

      // Build Recent Activity Feed
      const activityItems = [];
      sales.slice(0, 10).forEach(tx => {
        const createdAt = tx.attributes?.createdAt;
        const customer = tx.customer;
        const customerName =
          customer?.attributes?.profile?.displayName ||
          customer?.attributes?.profile?.firstName ||
          'Customer';
        const customerAvatar =
          customer?.profileImage?.attributes?.variants?.['square-small']?.url;
        const listing = tx.listing;
        const listingTitle = listing?.attributes?.title || 'Listing';
        const listingImages = listing?.images || [];
        const listingImage =
          listingImages[0]?.attributes?.variants?.['listing-card']?.url ||
          listingImages[0]?.attributes?.variants?.['square-small']?.url;
        const lastTransition = tx.attributes?.lastTransition || '';
        const payinTotal = tx.attributes?.payinTotal;
        const formattedPrice = payinTotal
          ? `${payinTotal.currency === 'EUR' ? '€' : '$'}${(payinTotal.amount / 100).toFixed(2)}`
          : null;

        if (lastTransition.includes('completed') || lastTransition.includes('delivered')) {
          activityItems.push({
            id: `completed-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'order_completed',
            title: `Order completed: ${listingTitle}`,
            customerName,
            customerAvatar,
            listingTitle,
            listingImage,
            formattedPrice,
            status: 'Completed',
            date: createdAt,
          });
        } else {
          activityItems.push({
            id: `order-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'order_received',
            title: `New order received: ${listingTitle}`,
            customerName,
            customerAvatar,
            listingTitle,
            listingImage,
            formattedPrice,
            status: 'Order Received',
            date: createdAt,
          });
        }

        if (customerName) {
          activityItems.push({
            id: `msg-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'message_received',
            title: `New message from ${customerName}`,
            customerName,
            customerAvatar,
            listingTitle,
            listingImage,
            status: 'Message',
            date: createdAt,
          });
        }
      });

      reviews.slice(0, 5).forEach(rev => {
        const author = rev.author;
        const authorName =
          author?.attributes?.profile?.displayName ||
          author?.attributes?.profile?.firstName ||
          'Buyer';
        const authorAvatar =
          author?.profileImage?.attributes?.variants?.['square-small']?.url;

        activityItems.push({
          id: `review-${rev.id?.uuid || rev.id}`,
          type: 'review_received',
          title: `Review received from ${authorName}`,
          authorName,
          authorAvatar,
          rating: rev.attributes?.rating || 5,
          content: rev.attributes?.content || '',
          date: rev.attributes?.createdAt,
        });
      });

      // Add recent listing events so sellers see their own active listings in the feed
      listings.slice(0, 5).forEach(l => {
        const createdAt = l.attributes?.createdAt;
        const title = l.attributes?.title || 'Listing';
        const images = l.images || [];
        const listingImage =
          images[0]?.attributes?.variants?.['listing-card']?.url ||
          images[0]?.attributes?.variants?.['square-small']?.url;
        const state = l.attributes?.state;

        activityItems.push({
          id: `listing-${l.id?.uuid || l.id}`,
          type: 'listing_created',
          title: `Listing: ${title}`,
          listingTitle: title,
          listingImage,
          status: state === 'published' ? 'Active' : state === 'draft' ? 'Draft' : 'Closed',
          date: createdAt,
        });
      });

      activityItems.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      const recentActivity = activityItems.slice(0, 100);

      return {
        activeListingsCount,
        totalListingsCount,
        ordersCount,
        messagesCount,
        totalEarningsAmount,
        currency,
        averageRating: Number(averageRating) || 0,
        reviewsCount,
        recentActivity,
        rawSales: sales,
        rawReviews: reviews,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// Redux Slice
const sellerDashboardSlice = createSlice({
  name: 'SellerDashboardPage',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    activeListingsCount: 0,
    totalListingsCount: 0,
    ordersCount: 0,
    messagesCount: 0,
    totalEarningsAmount: 0,
    currency: 'USD',
    averageRating: 0,
    reviewsCount: 0,
    recentActivity: [],
    rawSales: [],
    rawReviews: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSellerDashboardDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchSellerDashboardDataThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.activeListingsCount = action.payload.activeListingsCount;
        state.totalListingsCount = action.payload.totalListingsCount;
        state.ordersCount = action.payload.ordersCount;
        state.messagesCount = action.payload.messagesCount;
        state.totalEarningsAmount = action.payload.totalEarningsAmount;
        state.currency = action.payload.currency;
        state.averageRating = action.payload.averageRating;
        state.reviewsCount = action.payload.reviewsCount;
        state.recentActivity = action.payload.recentActivity;
        state.rawSales = action.payload.rawSales;
        state.rawReviews = action.payload.rawReviews;
      })
      .addCase(fetchSellerDashboardDataThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default sellerDashboardSlice.reducer;

// SSR loadData function
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(manageListingsLoadData(params, search, config)),
    dispatch(fetchSellerDashboardDataThunk(config)),
  ]);
};
